import TaskVitalsSection from './TaskVitalsSection'
import type { ComponentProps, ReactNode } from 'react'
import { useIsAdmin } from '../hooks/useAdminMode'
import TaskDetailLayout, {
  TaskDetailSkeleton,
  TaskDetailError,
  TaskDetailNotFound,
  type TaskDetailLayoutProps,
} from './TaskDetailLayout'
import { Link } from './nav'
import ScreenshotTimeline from './ScreenshotTimeline'
import McpErrorsSection, { type TaskError, type BackendTaskError } from './McpErrorsSection'
import { adminErrorUrl } from './activityFeed'
import LlmCallsSection, { type LlmJobMeta } from './LlmCallsSection'
import JourneyChronologySection from './JourneyChronologySection'
import TaskActionsSection, { type TaskActionMeta } from './TaskActionsSection'
import RetriggerLinks from './RetriggerLinks'
import type { TestRunScreenshot, TestRunLog } from '../data/projectTypes'

/** A log row as every task kind produces it (test-run, exploration, polish, plain task). */
export type TaskLogEntry = Pick<TestRunLog, 'id' | 'message' | 'log_type' | 'created_at'>

// Terminal statuses that warrant a "Why this failed" banner. 'infra-failed' is the test-run-specific
// failure status (reaped/zombie container, transient upload error); 'failed' covers every other kind.
// 'incomplete' and 'blocked' are the journey-run outcomes that are NOT failures but still ended without a
// verdict (see JourneyRunOutcome) — their whole point is that there is a specific reason to state, and
// their terminal transition files exactly that reason into task_errors.
const FAILED_STATUSES = new Set(['failed', 'infra-failed', 'incomplete', 'blocked'])

export interface TaskShellProps {
  /** Lowercase kind label used in the loading/not-found copy, e.g. "test run". */
  kindLabel: string
  loading: boolean
  /** When set, the error state renders with a retry button. */
  error?: string | null
  onRetry?: () => void
  /** Where the not-found state links back to. */
  notFoundBackTo: string
  notFoundBackLabel: string
  /** False once loading settled but the entity doesn't exist. */
  entityLoaded: boolean
  /** Layout chrome (back link, icon, title, status, meta, footer) for the loaded entity. The footer
   *  here is only the kind-specific actions (e.g. a retrigger button) */
  layout?: Omit<TaskDetailLayoutProps, 'children'>
  /** Kind-specific body sections. */
  children?: ReactNode
  // --- Uniform trailing sections, rendered the same way for every task kind ---
  screenshots?: TestRunScreenshot[]
  logs?: TaskLogEntry[]
  mcpErrors?: TaskError[] | null
  /** Backend server / LLM errors recorded during this task's run (backend_request_errors), shown in
   *  the same trailing Errors section as the task errors. Admin-only. */
  backendErrors?: BackendTaskError[] | null
  /** Every LLM call this task made, grouped by loop in the trailing "LLM Calls" section. Admin-only. */
  llmJobs?: LlmJobMeta[] | null
  /** Every ACTION this task performed — what it computed and which actions' output it consumed — in the
   *  trailing "Actions" section. Admin-only. `projectId` is needed to link a dependency that lives in
   *  another task (a followup's filing depending on the journey run's decision). */
  actions?: TaskActionMeta[] | null
  journeyChronology?: { taskId: string; active: boolean }
  projectId?: string
  /** JourneyRunOutcome.status when this shell is a test run; a `bug` is not a failure (statusForOutcome). */
  outcomeStatus?: string | null
  retrigger?: Omit<ComponentProps<typeof RetriggerLinks>, 'currentProjectId'> & { currentProjectId: string }
  testIdPrefix?: string
}

/**
 * The single rendering path for every task page (`/projects/:projectId/tasks/:taskId`),
 * whatever the task kind. It owns the uniform pipeline — loading skeleton, error, not-found,
 * the TaskDetailLayout chrome — and the trailing sections every task shares: guidance deps,
 * the screenshot/log timeline (logs are admin-only), MCP errors (admin-only diagnostics from
 * the container's error-reporting proxy), and retrigger links. Kind-specific sections come in
 * as children; kind-specific data arrives through props. Nothing about a task kind may fork
 * the pipeline itself — only the data varies.
 */
export default function TaskShell({
  kindLabel,
  loading,
  error,
  onRetry,
  notFoundBackTo,
  notFoundBackLabel,
  entityLoaded,
  layout,
  children,
  screenshots,
  logs,
  mcpErrors,
  backendErrors,
  llmJobs,
  actions,
  projectId,
  journeyChronology,
  outcomeStatus,
  retrigger,
  testIdPrefix = 'task',
}: TaskShellProps) {
  // TaskShell only renders inside TaskDetail, which is admin-only. Gate admin extras on real admin
  // status rather than the preview toggle (which isn't reachable from a task page). See useIsAdmin.
  const isAdmin = useIsAdmin()

  if (loading && !entityLoaded) {
    return <TaskDetailSkeleton label={`Loading ${kindLabel}`} testId={`${testIdPrefix}-loading`} />
  }
  if (error) {
    return (
      <TaskDetailError
        message={`Failed to load ${kindLabel}`}
        onRetry={onRetry ?? (() => {})}
        testId={`${testIdPrefix}-error`}
        retryTestId={`${testIdPrefix}-retry`}
      />
    )
  }
  if (!entityLoaded || !layout) {
    return (
      <TaskDetailNotFound
        message={`${kindLabel.charAt(0).toUpperCase()}${kindLabel.slice(1)} not found`}
        backTo={notFoundBackTo}
        backLabel={notFoundBackLabel}
        testId={`${testIdPrefix}-not-found`}
        backTestId="back-btn"
      />
    )
  }

  // One consolidated footer for every task kind: the shared (admin-only) "Update Guidance" button
  // first, then any kind-specific actions the page supplied (e.g. a retrigger button). Gated on
  // real admin status so a non-admin viewer with no kind-specific actions doesn't render an empty bar.
  const footer = layout.footer

  // "Why this failed" banner. When the task failed, surface the reason up front instead of leaving it
  // buried in the Errors diagnostics (or, historically, nowhere at all). Fires on both a plain 'failed'
  // status and the test-run-specific 'infra-failed' (a reaped/zombie container or a transient upload
  // error) — infra failures are the ones most likely to leave the reader staring at a bare status.
  // Prefer the run's OWN outcome-derived reason (source 'journey-run', filed by the run's terminal
  // transition from its structured JourneyRunOutcome — a typed statement naming the step and the cause),
  // then the driver's fatal-abort record (source 'driver', written right before the recording driver exits
  // non-zero — the no-spec / missing-guidance / crash paths that used to leave no trace), then the most
  // recent error row of any source so nothing terminal is left without a headline. Picking by source
  // rather than purely by time matters: the newest row is often an incidental MCP hiccup, not the cause.
  // Links to that error's detail page. Admin-only, like the error rows it reads.
  const failureError = outcomeStatus !== 'bug'
    && FAILED_STATUSES.has(layout.status ?? '')
    && mcpErrors
    && mcpErrors.length > 0
    ? (() => {
        const byTime = [...mcpErrors].sort((a, b) => (b.occurred_at ?? b.created_at).localeCompare(a.occurred_at ?? a.created_at))
        return byTime.find(e => e.source === 'journey-run') ?? byTime.find(e => e.source === 'driver') ?? byTime[0] ?? null
      })()
    : null

  return (
    <TaskDetailLayout {...layout} footer={footer}>
      {isAdmin && failureError && (
        <div className="task-detail__failure-banner" role="alert" data-testid={`${testIdPrefix}-failure-reason`}>
          <span className="task-detail__failure-banner-label">Why this failed</span>
          <p className="task-detail__failure-banner-message">
            {failureError.message ?? 'Unknown error'}
          </p>
          <Link
            to={adminErrorUrl(failureError.id)}
            className="task-detail__failure-banner-link"
            data-testid={`${testIdPrefix}-failure-error-link`}
            data-analytics-event="task-failure-error-open"
          >
            View error details
          </Link>
        </div>
      )}
      {children}

      {isAdmin && <TaskVitalsSection logs={logs ?? []} />}
      <ScreenshotTimeline
        screenshots={screenshots ?? []}
        logs={isAdmin ? ((logs ?? []).filter(log => log.log_type !== 'task-vitals') as TestRunLog[]) : []}
      />

      {isAdmin && ((mcpErrors && mcpErrors.length > 0) || (backendErrors && backendErrors.length > 0)) && (
        <McpErrorsSection errors={mcpErrors ?? []} backendErrors={backendErrors ?? []} classPrefix="task-detail" />
      )}

      {isAdmin && journeyChronology && <JourneyChronologySection {...journeyChronology} actions={actions ?? []} />}

      {isAdmin && llmJobs && llmJobs.length > 0 && (
        <LlmCallsSection jobs={llmJobs} classPrefix="task-detail" />
      )}

      {/* The provenance graph: what this task computed, and what each computation consumed. Sits next to
          the LLM calls because the two are complements — a call row holds the prompt, an action row holds
          what the answer was used for. */}
      {isAdmin && projectId && actions && (
        <TaskActionsSection actions={actions} projectId={projectId} classPrefix="task-detail" />
      )}

      {retrigger && <RetriggerLinks {...retrigger} />}
    </TaskDetailLayout>
  )
}
